// @ts-check
/* eslint-disable */
import { debounce } from './lodash-debounce.js';
import ResizeObserver from './resize-observer-polyfill.js'; //+
import { SIMPLE_BAR_SCROLL } from '../common/constants/events.js';
import { publish } from '../common/utils/pub-sub.js';

/* https://github.com/Grsmto/simplebar tailored for PES */

/** @typedef {import('../common/constants/events').SimpleBarScrollEvent} SimpleBarScrollEvent */

/**
 * (Content size - Scrollbar size) threshold that triggers scrollbar displaying
 * 15 should be enough to handle up to 175% zoom
 */
const SCROLLBAR_CONTENT_SIZE_ERROR = 15;

export default class SimpleBar {
  constructor(element, options) {
    this.el = element;
    this.flashTimeout;
    this.contentEl;
    this.scrollContentEl;
    this.dragOffset = { x: 0, y: 0 };
    this.isVisible = { x: true, y: true };
    this.scrollOffsetAttr = { x: 'scrollLeft', y: 'scrollTop' };
    this.sizeAttr = { x: 'offsetWidth', y: 'offsetHeight' };
    this.scrollSizeAttr = { x: 'scrollWidth', y: 'scrollHeight' };
    this.offsetAttr = { x: 'left', y: 'top' };
    this.globalObserver;
    this.mutationObserver;
    this.resizeObserver;
    this.currentAxis;
    this.options = { ...SimpleBar.defaultOptions, ...options };
    this.isRtl = this.options.direction === 'rtl';
    this.classNames = this.options.classNames;
    this.scrollbarWidth = scrollbarWidth();
    this.offsetSize = 20;
    this.flashScrollbar = this.flashScrollbar.bind(this);
    this.onDragY = this.onDragY.bind(this);
    this.onDragX = this.onDragX.bind(this);
    this.onScrollY = this.onScrollY.bind(this);
    this.onScrollX = this.onScrollX.bind(this);
    this.drag = this.drag.bind(this);
    this.onEndDrag = this.onEndDrag.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);

    this.recalculate = debounce(this.recalculate, 100, { leading: true });

    if (options.verticalOffsetTop) {
      this.options.verticalOffsetTop = parseInt(options.verticalOffsetTop, 10);
    } else {
      this.options.verticalOffsetTop = 0;
    }

    if (options.verticalOffsetBottom) {
      this.options.verticalOffsetBottom = parseInt(options.verticalOffsetBottom, 10);
    } else {
      this.options.verticalOffsetBottom = 0;
    }

    this.init();
  }

  static get defaultOptions() {
    return {
      autoHide: true,
      forceVisible: false,
      classNames: {
        content: 'simplebar-content',
        scrollContent: 'simplebar-scroll-content',
        scrollbar: 'simplebar-scrollbar',
        track: 'simplebar-track',
      },
      scrollbarMinSize: 25,
      scrollbarMaxSize: 0,
      direction: 'ltr',
    };
  }

  static initHtmlApi() {
    this.initDOMLoadedElements = this.initDOMLoadedElements.bind(this);

    // MutationObserver is IE11+
    if (typeof MutationObserver !== 'undefined') {
      // Mutation observer to observe dynamically added elements
      this.globalObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          Array.from(mutation.addedNodes).forEach((addedNode) => {
            if (addedNode.nodeType === 1) {
              if (addedNode.hasAttribute('data-simplebar')) {
                !addedNode.SimpleBar && new SimpleBar(addedNode, SimpleBar.getElOptions(addedNode));
              } else {
                Array.from(addedNode.querySelectorAll('[data-simplebar]')).forEach((el) => {
                  !el.SimpleBar && new SimpleBar(el, SimpleBar.getElOptions(el));
                });
              }
            }
          });

          Array.from(mutation.removedNodes).forEach((removedNode) => {
            if (removedNode.nodeType === 1) {
              if (removedNode.hasAttribute('data-simplebar')) {
                removedNode.SimpleBar && removedNode.SimpleBar.unMount();
              } else {
                Array.from(removedNode.querySelectorAll('[data-simplebar]')).forEach((el) => {
                  el.SimpleBar && el.SimpleBar.unMount();
                });
              }
            }
          });
        });
      });

      this.globalObserver.observe(document, { childList: true, subtree: true });
    }

    // Taken from jQuery `ready` function
    // Instantiate elements already present on the page
    if (
      document.readyState === 'complete' ||
      (document.readyState !== 'loading' && !document.documentElement.doScroll)
    ) {
      // Handle it asynchronously to allow scripts the opportunity to delay init
      window.setTimeout(this.initDOMLoadedElements.bind(this));
    } else {
      document.addEventListener('DOMContentLoaded', this.initDOMLoadedElements);
      window.addEventListener('load', this.initDOMLoadedElements);
    }
  }

  // From https://github.com/Grsmto/simplebar/blob/a01c510c54f565519fc9fbdad0fc126c2211c665/packages/simplebar/src/helpers.js#L2
  // Helper function to retrieve options from element attributes
  static getElOptions(el) {
    const options = Array.prototype.reduce.call(
      el.attributes,
      (acc, attribute) => {
        const option = attribute.name.match(/data-simplebar-(.+)/);
        if (option) {
          const key = option[1].replace(/\W+(.)/g, (x, chr) => chr.toUpperCase());
          switch (attribute.value) {
            case 'true':
              acc[key] = true;
              break;
            case 'false':
              acc[key] = false;
              break;
            case undefined:
              acc[key] = true;
              break;
            default:
              acc[key] = attribute.value;
          }
        }
        return acc;
      },
      {},
    );

    return options;
  }

  static removeObserver() {
    this.globalObserver.disconnect();
  }

  static initDOMLoadedElements() {
    document.removeEventListener('DOMContentLoaded', this.initDOMLoadedElements);
    window.removeEventListener('load', this.initDOMLoadedElements);

    Array.from(document.querySelectorAll('[data-simplebar]')).forEach((el) => {
      if (!el.SimpleBar) {
        new SimpleBar(el, SimpleBar.getElOptions(el));
      }
    });
  }

  init() {
    // Save a reference to the instance, so we know this DOM node has already been instantiated
    this.el.SimpleBar = this;

    this.initDOM();

    this.scrollbarX = this.trackX.querySelector(`.${this.classNames.scrollbar}`);
    this.scrollbarY = this.trackY.querySelector(`.${this.classNames.scrollbar}`);

    this.scrollContentEl.style[this.isRtl ? 'paddingLeft' : 'paddingRight'] = `${
      this.scrollbarWidth || this.offsetSize
    }px`;
    this.scrollContentEl.style.marginBottom = `-${this.scrollbarWidth * 2 || this.offsetSize}px`;
    this.contentEl.style.paddingBottom = `${this.scrollbarWidth || this.offsetSize}px`;

    if (this.scrollbarWidth !== 0) {
      this.contentEl.style[this.isRtl ? 'marginLeft' : 'marginRight'] = `-${this.scrollbarWidth}px`;
    }

    // Calculate content size
    this.recalculate();

    this.initListeners();
  }

  initDOM() {
    // make sure this element doesn't have the elements yet
    if (
      Array.from(this.el.children).filter((child) =>
        child.classList.contains(this.classNames.scrollContent),
      ).length
    ) {
      // assume that element has his DOM already initiated
      this.trackX = this.el.querySelector(`.${this.classNames.track}.horizontal`);
      this.trackY = this.el.querySelector(`.${this.classNames.track}.vertical`);
      this.scrollContentEl = this.el.querySelector(`.${this.classNames.scrollContent}`);
      this.contentEl = this.el.querySelector(`.${this.classNames.content}`);
    } else {
      // Prepare DOM
      this.scrollContentEl = document.createElement('div');
      this.contentEl = document.createElement('div');

      this.scrollContentEl.classList.add(this.classNames.scrollContent);
      this.contentEl.classList.add(this.classNames.content);

      while (this.el.firstChild) {
        this.contentEl.appendChild(this.el.firstChild);
      }

      this.scrollContentEl.appendChild(this.contentEl);
      this.el.appendChild(this.scrollContentEl);
    }

    if (!this.trackX || !this.trackY) {
      const track = document.createElement('div');
      const scrollbar = document.createElement('div');

      track.classList.add(this.classNames.track);
      scrollbar.classList.add(this.classNames.scrollbar);

      track.appendChild(scrollbar);

      this.trackX = track.cloneNode(true);
      this.trackX.classList.add('horizontal');

      this.trackY = track.cloneNode(true);
      this.trackY.classList.add('vertical');

      this.el.insertBefore(this.trackX, this.el.firstChild);
      this.el.insertBefore(this.trackY, this.el.firstChild);
    }

    this.el.setAttribute('data-simplebar', 'init');
  }

  initListeners() {
    // Event listeners
    if (this.options.autoHide) {
      this.el.addEventListener('mouseenter', this.onMouseEnter);
    }

    this.scrollbarY.addEventListener('mousedown', this.onDragY);
    this.scrollbarX.addEventListener('mousedown', this.onDragX);

    this.scrollContentEl.addEventListener('scroll', this.onScrollY);
    this.contentEl.addEventListener('scroll', this.onScrollX);

    // MutationObserver is IE11+
    if (typeof MutationObserver !== 'undefined') {
      const scrollbarSelector = `.${this.classNames.scrollbar}`;
      // create an observer instance
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Avoid infinite firings - Don't recalculate when scrollbar is changed
          if (mutation.target.nodeType === 1 && mutation.target.matches(scrollbarSelector)) {
            return;
          }

          if (this.isChildNode(mutation.target) || mutation.addedNodes.length) {
            this.recalculate();
          }
        });
      });

      // pass in the target node, as well as the observer options
      this.mutationObserver.observe(this.el, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    this.resizeObserver = new ResizeObserver(this.recalculate.bind(this));
    this.resizeObserver.observe(this.el);
  }

  removeListeners() {
    // Event listeners
    if (this.options.autoHide) {
      this.el.removeEventListener('mouseenter', this.onMouseEnter);
    }

    this.scrollbarX.removeEventListener('mousedown', this.onDragX);
    this.scrollbarY.removeEventListener('mousedown', this.onDragY);

    this.scrollContentEl.removeEventListener('scroll', this.onScrollY);
    this.contentEl.removeEventListener('scroll', this.onScrollX);

    this.mutationObserver.disconnect();
    this.resizeObserver.disconnect();
  }

  onDragX(e) {
    this.onDrag(e, 'x');
  }

  onDragY(e) {
    this.onDrag(e, 'y');
  }

  /**
   * on scrollbar handle drag
   */
  onDrag(e, axis = 'y') {
    // Preventing the event's default action stops text being
    // selectable during the drag.
    e.preventDefault();

    const scrollbar = axis === 'y' ? this.scrollbarY : this.scrollbarX;

    // Measure how far the user's mouse is from the top of the scrollbar drag handle.
    const eventOffset = axis === 'y' ? e.pageY : e.pageX;

    this.dragOffset[axis] = eventOffset - scrollbar.getBoundingClientRect()[this.offsetAttr[axis]];
    this.currentAxis = axis;

    document.addEventListener('mousemove', this.drag);
    document.addEventListener('mouseup', this.onEndDrag);
  }

  /**
   * Drag scrollbar handle
   */
  drag(e) {
    let eventOffset;
    let track;
    let scrollEl;

    e.preventDefault();

    if (this.currentAxis === 'y') {
      eventOffset = e.pageY;
      track = this.trackY;
      scrollEl = this.scrollContentEl;
    } else {
      eventOffset = e.pageX;
      track = this.trackX;
      scrollEl = this.contentEl;
    }

    // Calculate how far the user's mouse is from the top/left of the scrollbar (minus the dragOffset).
    const dragPos =
      eventOffset -
      track.getBoundingClientRect()[this.offsetAttr[this.currentAxis]] -
      this.dragOffset[this.currentAxis];

    // Convert the mouse position into a percentage of the scrollbar height/width.
    const dragPerc = dragPos / track[this.sizeAttr[this.currentAxis]];

    // Scroll the content by the same percentage.
    const scrollPos = dragPerc * this.contentEl[this.scrollSizeAttr[this.currentAxis]];

    scrollEl[this.scrollOffsetAttr[this.currentAxis]] = scrollPos;
  }

  /**
   * End scroll handle drag
   */
  onEndDrag() {
    document.removeEventListener('mousemove', this.drag);
    document.removeEventListener('mouseup', this.onEndDrag);
  }

  previousHandleYOffset = 0;

  /**
   * Resize scrollbar
   */
  resizeScrollbar(axis = 'y') {
    let track;
    let scrollbar;
    let scrollOffset;
    let contentSize;
    let scrollbarSize;

    if (axis === 'x') {
      track = this.trackX;
      scrollbar = this.scrollbarX;
      scrollOffset = this.contentEl[this.scrollOffsetAttr[axis]]; // Either scrollTop() or scrollLeft().
      contentSize = this.contentSizeX;
      scrollbarSize = this.scrollbarXSize;
    } else {
      // 'y'
      track = this.trackY;
      scrollbar = this.scrollbarY;
      scrollOffset = this.scrollContentEl[this.scrollOffsetAttr[axis]]; // Either scrollTop() or scrollLeft().
      contentSize = this.contentSizeY;
      scrollbarSize = this.scrollbarYSize;
    }

    const scrollbarRatio = scrollbarSize / contentSize;
    const scrollPercent = scrollOffset / (contentSize - scrollbarSize);
    // Calculate new height/position of drag handle.
    let handleSize = Math.max(~~(scrollbarRatio * scrollbarSize), this.options.scrollbarMinSize);

    if (this.options.scrollbarMaxSize) {
      handleSize = Math.min(handleSize, this.options.scrollbarMaxSize);
    }

    const handleOffset = ~~((scrollbarSize - handleSize) * scrollPercent);

    // Set isVisible to false if scrollbar is not necessary (content is shorter than wrapper)
    this.isVisible[axis] = contentSize - scrollbarSize > SCROLLBAR_CONTENT_SIZE_ERROR;

    if (this.isVisible[axis] || this.options.forceVisible) {
      track.style.visibility = 'visible';

      if (this.options.forceVisible) {
        scrollbar.style.visibility = 'hidden';
      } else {
        scrollbar.style.visibility = 'visible';
      }

      if (axis === 'x') {
        scrollbar.style.left = `${handleOffset}px`;
        scrollbar.style.width = `${handleSize}px`;
      } else {
        scrollbar.style.top = `${handleOffset + this.options.verticalOffsetTop}px`;
        scrollbar.style.height = `${
          handleSize - (this.options.verticalOffsetTop + this.options.verticalOffsetBottom)
        }px`;
      }
    } else {
      track.style.visibility = 'hidden';
    }

    if (axis === 'y' && this.previousHandleYOffset !== handleOffset) {
      /** @type {SimpleBarScrollEvent} */
      const scrollEvent = { yOffset: handleOffset };

      publish(SIMPLE_BAR_SCROLL, scrollEvent);

      this.previousHandleYOffset = handleOffset;
    }
  }

  /**
   * On scroll event handling
   */
  onScrollX() {
    this.flashScrollbar('x');
  }

  onScrollY() {
    this.flashScrollbar('y');
  }

  /**
   * On mouseenter event handling
   */
  onMouseEnter() {
    this.flashScrollbar('x');
    this.flashScrollbar('y');
  }

  /**
   * Flash scrollbar visibility
   */
  flashScrollbar(axis = 'y') {
    this.resizeScrollbar(axis);
    this.showScrollbar(axis);
  }

  /**
   * Show scrollbar
   */
  showScrollbar(axis = 'y') {
    if (!this.isVisible[axis]) {
      this.hideScrollbar(axis);
      return;
    }

    if (axis === 'x') {
      this.scrollbarX.classList.add('visible');
    } else {
      this.scrollbarY.classList.add('visible');
    }

    if (!this.options.autoHide) {
      return;
    }

    if (typeof this.flashTimeout === 'number') {
      window.clearTimeout(this.flashTimeout);
    }

    this.flashTimeout = window.setTimeout(this.hideScrollbars.bind(this), 1000);
  }

  /**
   * Hide Scrollbar
   */
  hideScrollbars() {
    this.scrollbarX.classList.remove('visible');
    this.scrollbarY.classList.remove('visible');

    if (typeof this.flashTimeout === 'number') {
      window.clearTimeout(this.flashTimeout);
    }
  }

  hideScrollbar(axis = 'y') {
    if (axis === 'x') {
      this.scrollbarX.classList.remove('visible');
    } else {
      this.scrollbarY.classList.remove('visible');
    }

    if (typeof this.flashTimeout === 'number') {
      window.clearTimeout(this.flashTimeout);
    }
  }

  /**
   * Recalculate scrollbar
   */
  recalculate() {
    this.contentSizeX = this.contentEl[this.scrollSizeAttr.x];
    this.contentSizeY =
      this.contentEl[this.scrollSizeAttr.y] - (this.scrollbarWidth || this.offsetSize);
    this.scrollbarXSize = this.trackX[this.sizeAttr.x];
    this.scrollbarYSize = this.trackY[this.sizeAttr.y];

    this.resizeScrollbar('x');
    this.resizeScrollbar('y');

    if (!this.options.autoHide) {
      this.showScrollbar('x');
      this.showScrollbar('y');
    }
  }

  /**
   * Getter for original scrolling element
   */
  getScrollElement(axis = 'y') {
    return axis === 'y' ? this.scrollContentEl : this.contentEl;
  }

  /**
   * Getter for content element
   */
  getContentElement() {
    return this.contentEl;
  }

  /**
   * UnMount mutation observer and delete SimpleBar instance from DOM element
   */
  unMount() {
    this.removeListeners();
    this.el.SimpleBar = null;
  }

  /**
   * Recursively walks up the parent nodes looking for this.el
   */
  isChildNode(el) {
    if (el === null) return false;
    if (el === this.el) return true;

    return this.isChildNode(el.parentNode);
  }
}

/**
 * IMPORTS
 */

function scrollbarWidth() {
  if (typeof document === 'undefined') {
    return 0;
  }

  const { body } = document;
  const box = document.createElement('div');
  const boxStyle = box.style;
  let width;

  boxStyle.position = 'absolute';
  boxStyle.top = boxStyle.left = '-9999px';
  boxStyle.width = boxStyle.height = '100px';
  boxStyle.overflow = 'scroll';

  body.appendChild(box);

  width = box.offsetWidth - box.clientWidth;

  body.removeChild(box);

  return width;
}

/**
 * HTML API
 */
SimpleBar.initHtmlApi();
